import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Eye,
  ChevronDown,
  ChevronUp,
  Search,
  MessageSquare,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ENTITY_TYPE_OPTIONS,
  entityTypeLabel,
} from '@/features/votes/entity-votes';
import {
  useEntityVotes,
  useExportEntityVotes,
  type VoteFilter,
} from '@/features/votes/useEntityVotes';
import { entityTypeSchema } from '@/lib/route-search';
import { cn } from '@/lib/utils';

import type { MultiValue } from 'react-select';

const VOTE_TYPE_FILTER_OPTIONS = [
  { value: 'upvote', label: 'Upvote' },
  { value: 'downvote', label: 'Downvote' },
] as const;

type VoteTypeOption = (typeof VOTE_TYPE_FILTER_OPTIONS)[number];

export default function EntityVotesTable() {
  const navigate = useNavigate({ from: '/entity-votes' });
  const routeSearch = useSearch({ from: '/entity-votes' });
  const page = routeSearch.page;
  const search = routeSearch.search;
  const entityType = routeSearch.entityType;
  const selectedVoteTypeOptions = VOTE_TYPE_FILTER_OPTIONS.filter((option) =>
    routeSearch.voteTypes.includes(option.value)
  );
  const filter: VoteFilter =
    routeSearch.voteTypes.length === 1 ? routeSearch.voteTypes[0] : 'all';
  const [expandedReasons, setExpandedReasons] = useState<Set<number>>(
    new Set()
  );
  const [searchInput, setSearchInput] = useState(search);
  const limit = 50;

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const handleSearch = () => {
    navigate({
      search: { ...routeSearch, page: 1, search: searchInput },
    });
  };

  const { data, isLoading, error, refetch, isRefetching } = useEntityVotes(
    page,
    limit,
    entityType,
    filter,
    search
  );

  const exportVotes = useExportEntityVotes();
  const toast = useToast();

  const totalPages = data?.total ? Math.ceil(data.total / limit) : 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getVoteIcon = (direction: number) => {
    return direction > 0 ? (
      <ThumbsUp className="h-4 w-4 text-green-600" />
    ) : (
      <ThumbsDown className="h-4 w-4 text-red-600" />
    );
  };

  const getVoteBadge = (direction: number) => {
    return direction > 0 ? (
      <Badge
        variant="default"
        className="bg-green-100 text-green-800 border-green-200"
      >
        Upvote
      </Badge>
    ) : (
      <Badge
        variant="destructive"
        className="bg-red-100 text-red-800 border-red-200"
      >
        Downvote
      </Badge>
    );
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const toggleReasonExpansion = (voteId: number) => {
    setExpandedReasons((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(voteId)) {
        newSet.delete(voteId);
      } else {
        newSet.add(voteId);
      }
      return newSet;
    });
  };

  const isReasonExpanded = (voteId: number) => expandedReasons.has(voteId);

  const handleVoteTypeChange = (options: MultiValue<VoteTypeOption>) => {
    navigate({
      search: {
        ...routeSearch,
        page: 1,
        voteTypes: options.map((option) => option.value),
      },
    });
  };

  const handleEntityTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    navigate({
      search: {
        ...routeSearch,
        page: 1,
        entityType: entityTypeSchema.parse(e.target.value),
      },
    });
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">
            Error Loading Entity Votes
          </CardTitle>
          <CardDescription>
            {error instanceof Error
              ? error.message
              : 'An unknown error occurred'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Other Votes</CardTitle>
              <CardDescription>
                View user votes on other entities
                {data?.total && ` (${data.total} total)`}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() =>
                  exportVotes.mutate(
                    { entityType, filter, search },
                    {
                      onError: (exportError) =>
                        toast.error('Export failed', exportError.message),
                    }
                  )
                }
                variant="outline"
                size="sm"
                disabled={exportVotes.isPending}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV (newest 500)
              </Button>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isRefetching}
              >
                <RefreshCw
                  className={cn('h-4 w-4 mr-2', isRefetching && 'animate-spin')}
                />
                Refresh
              </Button>
            </div>
          </div>

          <div className="flex gap-4 flex-wrap">
            <div className="min-w-0 flex-1 sm:min-w-[220px]">
              <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">
                Search by User Email
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search user email... (press Enter)"
                  value={searchInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchInput(e.target.value);
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="min-w-0 sm:min-w-[180px]">
              <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">
                Entity Type
              </label>
              <Select
                value={entityType}
                onChange={handleEntityTypeChange}
                className="h-10"
              >
                {ENTITY_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>

            <MultiSelectFilter<VoteTypeOption>
              label="Vote Type Filter"
              options={VOTE_TYPE_FILTER_OPTIONS}
              value={selectedVoteTypeOptions}
              onChange={handleVoteTypeChange}
              placeholder="Filter by vote type..."
              classNamePrefix="vote-type-select"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vote</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Entity Type</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Reasoning</TableHead>
                      <TableHead>Tags</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.votes.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className="text-center py-8 text-muted-foreground"
                        >
                          No entity votes found
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.votes.map((vote) => (
                        <TableRow key={vote.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getVoteIcon(vote.direction)}
                              {getVoteBadge(vote.direction)}
                              {vote.has_notes && (
                                <div title="Has notes">
                                  <MessageSquare className="h-4 w-4 text-blue-600 fill-blue-600 stroke-[2.5]" />
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="text-sm font-medium">
                                {vote.user_email}
                              </p>
                              {vote.user_name && (
                                <p className="text-xs text-muted-foreground">
                                  {vote.user_name}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {entityTypeLabel(vote.entity_type)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="text-sm font-medium max-w-48 truncate">
                                {vote.entity_name || 'Unnamed'}
                              </p>
                              <p className="text-xs text-muted-foreground font-mono">
                                {vote.entity_id.slice(0, 8)}...
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-48">
                            {vote.reasoning ? (
                              <div className="space-y-1">
                                <p className="text-sm whitespace-pre-wrap break-words">
                                  {isReasonExpanded(vote.id)
                                    ? vote.reasoning
                                    : truncateText(vote.reasoning, 50)}
                                </p>
                                {vote.reasoning.length > 50 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() =>
                                      toggleReasonExpansion(vote.id)
                                    }
                                  >
                                    {isReasonExpanded(vote.id) ? (
                                      <>
                                        <ChevronUp className="h-3 w-3 mr-1" />
                                        Show less
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="h-3 w-3 mr-1" />
                                        Show more
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">
                                No reasoning
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {vote.tags && vote.tags.length > 0 ? (
                              <div className="flex flex-wrap gap-1 max-w-48">
                                {vote.tags.map((tag, idx) => (
                                  <Badge
                                    key={idx}
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {tag.replace(/_/g, ' ')}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">
                                No tags
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {vote.status ? (
                              <Badge variant="outline" className="text-xs">
                                {vote.status}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">
                                -
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(vote.created_at)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                navigate({
                                  to: '/entity-votes/$voteId',
                                  params: { voteId: vote.id.toString() },
                                  search: {
                                    returnTo: routeSearch,
                                    discussion: false,
                                  },
                                })
                              }
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate({
                          search: {
                            ...routeSearch,
                            page: Math.max(1, page - 1),
                          },
                        })
                      }
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate({
                          search: {
                            ...routeSearch,
                            page: Math.min(totalPages, page + 1),
                          },
                        })
                      }
                      disabled={page === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
